/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";

import { EndpointModel } from "../../Common/EndpointModel";
import { Panel, PanelNote } from "../../../../ui/components/Panel";
import { T } from "@adapptio/lib-components";
import { termsEditorEndpointEditor } from "../../../../terms/endpointEditor";
import { offEvent, onEvent } from "@adapptio/lib-shared";
import {
	EndpointEditorEditSelection,
	TEndpointEditSelectionItem
} from "../../Common/EndpointEditorEditSelection";
import { EndpointEditorEditView } from "../EndpointEditorEditView";
import { InspectEndpointSelectionItem } from "../../Common/InspectEndpointSelectionItem";

const pathFlowNode = [ "$flowNode" ];

function getSelectedModel(model: EndpointModel, editSelection: EndpointEditorEditSelection) {
	const selectedItems = editSelection.getItems();

	if (selectedItems.length !== 1) {
		return null;
	}

	const node = model.getDesignContext().getNodeById(selectedItems[0].nodeId);

	if (node && node.schema.name === "flowNode") {
		return node;
	} else {
		return null;
	}
}

function getSelectedItemOrEvent(model: EndpointModel): TEndpointEditSelectionItem {
	const selection = model.getSelection();
	const selectedItems = selection.getItems();

	if (selectedItems?.length !== 1) {
		return null;
	}

	return selectedItems[0];
}

export interface IEndpointEditorInspectorProps {
	view: EndpointEditorEditView;
}

export const EndpointEditorEditInspector: React.FunctionComponent<IEndpointEditorInspectorProps> = ({
	view
}) => {
	const model = view.getEndpointModel();

	const editSelection = useMemo(() => model.getSelection(), [ model ]);
	const [ areMultipleItemsSelected, setAreMultipleItemsSelected ] = useState(
		() => editSelection.getItems().length > 1
	);

	const [ selectedItemOrEvent, setSelectedItemOrEvent ] = useState<TEndpointEditSelectionItem>(() =>
		getSelectedItemOrEvent(model)
	);

	const refreshSelectedItemOrEvent = useCallback(() => {
		const item = getSelectedItemOrEvent(model);
		setSelectedItemOrEvent(item ? { ...item } : item);
	}, [ model, setSelectedItemOrEvent ]);

	const getNodeById = useCallback(
		(nodeId: number) => model.getDesignContext().getNodeById(nodeId),
		[ model ]
	);

	const selectedModel = useMemo(
		() => getNodeById(selectedItemOrEvent?.nodeId) || null,
		[ selectedItemOrEvent, getNodeById ]
	);

	useEffect(() => {
		const handleModelChange = () => {
			refreshSelectedItemOrEvent();
		};

		const handleSelectionChange = () => {
			setAreMultipleItemsSelected(editSelection.getItems().length > 1);
			refreshSelectedItemOrEvent();
		};

		onEvent(model.onModelChange, handleModelChange);
		onEvent(editSelection.onChange, handleSelectionChange);

		return () => {
			offEvent(model.onModelChange, handleModelChange);
			offEvent(editSelection.onChange, handleSelectionChange);
		};
	}, [ model, editSelection ]);

	return (
		<Panel scroll={true}>
			{
				<InspectEndpointSelectionItem
					selectionItem={selectedItemOrEvent}
					getNodeById={getNodeById}
					view={view}
				/>
			}
			{areMultipleItemsSelected ? (
				<PanelNote>
					<p>
						<T
							domain="editor"
							term={termsEditorEndpointEditor.inspector.cannotEditMultipleNodes}
						/>
					</p>
				</PanelNote>
			) : null}
			{!selectedModel && !areMultipleItemsSelected ? (
				<PanelNote>
					<p>
						<T domain="editor" term={termsEditorEndpointEditor.inspector.selectNodeToInspect} />
					</p>
				</PanelNote>
			) : null}
		</Panel>
	);
};
